import { RelayStatus, RelayStatusField } from '@quromedical/aws';
import { compact } from '@quromedical/utils';
import { useMemo } from 'react';
import { trpc } from 'trpc';

export interface UseRelayStatusResult {
  data: RelayStatus[];
  hasError: boolean;
  isLoading: boolean;
  refetch: () => void;
}

export interface RelayStatusIssues {
  hasAny: boolean;
  mbs: boolean;
  relay: boolean;
  noBluetooth: boolean;
  lowBattery: boolean;
  lowStorage: boolean;
  poorNetwork: boolean;
}

const networkThreshold = 2;
const memoryThreshold = 8000;
const batteryThreshold = 10;

const getValue = (field?: RelayStatusField<number>): number => field?.value || 0;

export const getRelayStatusIssues = (status?: RelayStatus): RelayStatusIssues | undefined => {
  if (!status) {
    return undefined;
  }

  const {
    bluetoothGattConnectedDevices,
    availableStorageMB,
    batteryIsCharging,
    batteryPercent,
    cellularNetworkLevel,
    mbsAppStatus,
  } = status;

  const poorNetwork = getValue(cellularNetworkLevel) < networkThreshold;
  const lowStorage = getValue(availableStorageMB) < memoryThreshold;
  const lowBattery =
    batteryIsCharging?.value === false && getValue(batteryPercent) < batteryThreshold;
  const noBluetooth = bluetoothGattConnectedDevices?.value?.length === 0;

  const relay = poorNetwork || lowBattery || lowStorage || noBluetooth;

  const mbsNotification = !mbsAppStatus?.value?.toLowerCase().includes('monitoring');

  // We have seen use cases where the MBS app status is monitoring but the timestamp is old and the
  // app is not updating notifications.
  const tenMinutes = 10 * 60 * 1000;
  const isMBSOutOfDate = !!mbsAppStatus && Date.now() - mbsAppStatus.timestamp > tenMinutes;

  const mbs = mbsNotification || isMBSOutOfDate;
  const hasAny = relay || mbs;

  return {
    hasAny,
    mbs,
    relay,
    noBluetooth,
    lowBattery,
    lowStorage,
    poorNetwork,
  };
};

export const useRelayStatuses = (deviceIds: string[]): UseRelayStatusResult => {
  const ids = deviceIds.join(',');

  const { data, error, isFetching, refetch } = trpc.context.relay.getStatusById.useQuery({
    deviceIds: ids,
  });

  const memoData = useMemo(
    () => (data?.relayStatuses ? (compact(data.relayStatuses) as RelayStatus[]) : []),
    [data?.relayStatuses]
  );

  return { data: memoData, hasError: !!error, isLoading: isFetching, refetch };
};
